/** 海外上架页仅允许 Win10 / Win11（对齐 spec §7.1.2 与演示原型） */
const INTL_RUN_ENV_NAME_SET = new Set(["win10", "win11"]);
const INTL_LEGACY_LANGUAGE_MULTI_ID = 4;

export function getIntlRunEnvOptions(systemEnv = []) {
  if (!Array.isArray(systemEnv)) {
    return [];
  }

  const matched = systemEnv.filter((item) =>
    INTL_RUN_ENV_NAME_SET.has(String(item?.name || "").trim().toLowerCase())
  );
  if (matched.length) {
    return matched;
  }

  // config 名称未命中时，按现网常见 id 兜底：7=Win10，8=Win11
  return systemEnv.filter((item) => [7, 8].includes(Number(item.id)));
}

export function getIntlOsBitOptions() {
  return [{ value: "64", label: "64 位" }];
}

export function normalizeIntlRunEnvValues(runEnvs = [], systemEnv = []) {
  const allowed = new Set(
    getIntlRunEnvOptions(systemEnv).map((item) => String(item.id))
  );
  return (runEnvs || [])
    .map((item) => String(item).trim())
    .filter((item) => allowed.has(item));
}

export function normalizeIntlOsBitValues(osOsbit = []) {
  const values = (osOsbit || []).map((item) => String(item).trim()).filter(Boolean);
  return values.includes("64") ? ["64"] : values.length ? values.filter((v) => v === "64") : [];
}

export function isValidIntlFeeValue(fee) {
  const value = Number(fee);
  return [1, 2, 3, 4].includes(value);
}

/** 有效类目 ID：跳过 0 / 空（audit 草稿未填时常为 0，Select 无法映射会显示裸数字） */
export function resolveIntlAppCateId(source = {}) {
  if (!source || typeof source !== "object") {
    return undefined;
  }

  const candidates = [source.app_cate_d, source.app_cate_id];

  for (const id of candidates) {
    if (id === undefined || id === null || id === "") {
      continue;
    }
    const num = Number(id);
    if (Number.isFinite(num) && num > 0) {
      return num;
    }
  }

  return undefined;
}

/** 按优先级合并类目：info/getbaseinfo / 创单跳转 state 优先于 getdetail/audit */
export function resolveIntlAppCateIdFromSources(...sources) {
  for (const source of sources) {
    const id = resolveIntlAppCateId(source);
    if (id !== undefined) {
      return id;
    }
  }
  return undefined;
}

export function checkImageUploadStatus(fileList) {
  if (!Array.isArray(fileList) || !fileList.length) {
    return false;
  }
  return fileList.every((item) => item?.response?.pic_url);
}

export function mapImageUrlsToFileList(urls, namePrefix) {
  if (!urls) {
    return [];
  }
  const urlText = Array.isArray(urls) ? urls.join(",") : urls;
  return urlText
    .split(",")
    .filter(Boolean)
    .map((url, index) => ({
      uid: `${namePrefix}-${index}`,
      type: "image/png",
      response: { pic_url: url },
      name: `${namePrefix}-${index}.png`,
      status: "done",
    }));
}

export function mapSoftSnapshotToFileList(snapshots, code) {
  const urls = Array.isArray(snapshots)
    ? snapshots
    : snapshots
      ? String(snapshots).split(",")
      : [];
  return urls.filter(Boolean).map((url, index) => ({
    uid: `${code}-snap-${index}`,
    type: "image/png",
    response: { pic_url: url },
    name: `${code}-snap-${index}.png`,
    status: "done",
  }));
}

export function mapFileListToImageUrls(fileList) {
  if (!Array.isArray(fileList) || !fileList.length) {
    return "";
  }
  return fileList.map((item) => item.response.pic_url).join(",");
}

export function mapFileListToSoftSnapshot(fileList) {
  if (!Array.isArray(fileList) || !fileList.length) {
    return [];
  }
  return fileList.map((item) => item.response.pic_url).filter(Boolean);
}

export function mapRealFileNameToFileList(realFileName) {
  if (!realFileName) {
    return [];
  }
  return [
    {
      uid: -1,
      type: "application/octet-stream",
      response: realFileName,
      name: realFileName,
      status: "done",
    },
  ];
}

export function mapExtendedLangItemToForm(item = {}) {
  const code = item.language_code || "lang";
  return {
    soft_name: item.soft_name || "",
    soft_baike_name: item.soft_baike_name || "",
    soft_brief: item.soft_brief || "",
    soft_logo: mapImageUrlsToFileList(item.soft_logo, `soft_logo_${code}`),
    soft_snapshot: mapSoftSnapshotToFileList(item.soft_snapshot, code),
    video_url: item.video_url || "",
    soft_description: item.soft_description || "",
  };
}

/** 国际上架页默认语言：优先 en / 英语，否则取枚举首项 */
export function resolveIntlDefaultLanguageCode(languageOptions = []) {
  const byCode = languageOptions.find((item) => item.language_code === "en");
  if (byCode?.language_code) {
    return byCode.language_code;
  }

  const byName = languageOptions.find((item) => {
    const name = String(item.language_name || "").trim();
    return name === "英语" || /^english$/i.test(name);
  });
  if (byName?.language_code) {
    return byName.language_code;
  }

  return languageOptions[0]?.language_code || "en";
}

/** 是否已有上架页语言配置（创单 app_name  alone 不算） */
export function hasIntlRegisterLanguageConfig(detail = {}) {
  const multi = Array.isArray(detail.soft_multi_language) ? detail.soft_multi_language : [];
  return (
    multi.length > 0 ||
    Boolean(detail.logo_pic) ||
    Boolean(detail.running_pic) ||
    Boolean(detail.feature)
  );
}

/**
 * 已落库/创单过的语言名称不可再改（与当前是否默认无关）。
 * - soft_multi_language 中出现过的 language_code → 锁定
 * - 仅有创单 app_name、尚未上架保存：锁定当时的默认语种（multi[0] 或英语兜底）
 * - 新勾选且从未提交的语种 → 可首次填写（即便被设为当前默认也可改名，直到提交）
 */
export function getIntlLockedLanguageNameCodes(detail = {}, languageOptions = []) {
  const locked = new Set();
  const multi = Array.isArray(detail.soft_multi_language) ? detail.soft_multi_language : [];

  multi.forEach((item) => {
    if (item?.language_code) {
      locked.add(item.language_code);
    }
  });

  if (detail.app_name) {
    const createDefaultCode =
      multi[0]?.language_code || resolveIntlDefaultLanguageCode(languageOptions);
    if (createDefaultCode) {
      locked.add(createDefaultCode);
    }
  }

  return locked;
}

export function isIntlLanguageNameLocked(
  detail = {},
  languageCode,
  { languageOptions = [] } = {}
) {
  if (!languageCode) {
    return false;
  }
  return getIntlLockedLanguageNameCodes(detail, languageOptions).has(languageCode);
}

/** 从 getdetail 推导默认语言、已选语言与扩展语言表单 */
export function deriveLanguageStateFromDetail(detail = {}, languageOptions = []) {
  const multi = Array.isArray(detail.soft_multi_language) ? detail.soft_multi_language : [];
  const defaultCodeFromMulti = multi[0]?.language_code;
  const extCodes = multi
    .slice(defaultCodeFromMulti ? 1 : 0)
    .map((item) => item.language_code)
    .filter(Boolean);
  const extCodeSet = new Set(extCodes);

  const fallbackCode = resolveIntlDefaultLanguageCode(languageOptions);
  const hasSavedLanguages = hasIntlRegisterLanguageConfig(detail);

  let defaultLanguageCode = fallbackCode;
  if (hasSavedLanguages) {
    if (defaultCodeFromMulti) {
      defaultLanguageCode = defaultCodeFromMulti;
    } else if (extCodes.length) {
      defaultLanguageCode =
        languageOptions.find((item) => !extCodeSet.has(item.language_code))
          ?.language_code || fallbackCode;
    } else {
      // 仅根级素材、无扩展语言：BE 未存 default language_code，国际基线按英语回显
      defaultLanguageCode = fallbackCode;
    }
  }

  const selectedLanguageCodes = hasSavedLanguages
    ? [defaultLanguageCode, ...extCodes.filter((code) => code !== defaultLanguageCode)]
    : fallbackCode
      ? [fallbackCode]
      : [];

  const extendedLangFields = {};
  multi.slice(defaultCodeFromMulti ? 1 : 0).forEach((item) => {
    if (!item.language_code) {
      return;
    }
    extendedLangFields[item.language_code] = mapExtendedLangItemToForm(item);
  });

  return {
    defaultLanguageCode,
    selectedLanguageCodes,
    extendedLangFields,
  };
}

/** getdetail → 表单初始值 */
export function mapDetailToRegisterForm(detail = {}, languageOptions = []) {
  const { extendedLangFields } = deriveLanguageStateFromDetail(detail, languageOptions);

  return {
    app_name: detail.app_name || "",
    app_cate_id: resolveIntlAppCateId(detail),
    app_version: detail.app_version || "",
    upload_type: detail.upload_type ?? 0,
    soft_download_url: detail.soft_download_url || "",
    real_file_name: mapRealFileNameToFileList(detail.real_file_name),
    certificate_pic: mapImageUrlsToFileList(detail.certificate_pic, "certificate_pic"),
    logo_pic: mapImageUrlsToFileList(detail.logo_pic, "logo_pic"),
    running_pic: mapImageUrlsToFileList(detail.running_pic, "running_pic"),
    feature: detail.feature || "",
    new_feature: detail.new_feature || "",
    install_params: detail.install_params || "",
    run_envs: detail.run_envs?.split(",").filter(Boolean) || [],
    os_osbit: detail.os_osbit?.split(",").filter(Boolean) || [],
    fee: detail.fee ?? undefined,
    free_trial_days: detail.free_trial_days ?? 0,
    extendedLang: extendedLangFields,
  };
}

export function pickDetailMeta(detail = {}) {
  return {
    id: detail.id,
    app_id: detail.app_id,
    app_pkg_key: detail.app_pkg_key,
    white_list_region: Array.isArray(detail.white_list_region) ? detail.white_list_region : [],
    soft_multi_language: Array.isArray(detail.soft_multi_language)
      ? detail.soft_multi_language
      : [],
  };
}

export function getLanguageLabel(languageOptions, code) {
  return (
    languageOptions.find((item) => item.language_code === code)?.language_name || code
  );
}

export function isDefaultLanguageTabComplete(formValues = {}, defaultLanguageCode) {
  if (!defaultLanguageCode) {
    return false;
  }
  return Boolean(formValues.app_name) &&
    checkImageUploadStatus(formValues.logo_pic) &&
    checkImageUploadStatus(formValues.running_pic) &&
    Boolean(formValues.feature);
}

export function isExtendedLanguageTabComplete(formValues = {}, languageCode) {
  const item = formValues.extendedLang?.[languageCode];
  if (!item) {
    return false;
  }
  return Boolean(item.soft_name) &&
    checkImageUploadStatus(item.soft_logo) &&
    checkImageUploadStatus(item.soft_snapshot) &&
    Boolean(item.soft_description);
}

export function isLanguageTabPending(formValues, languageCode, defaultLanguageCode) {
  if (languageCode === defaultLanguageCode) {
    return !isDefaultLanguageTabComplete(formValues, defaultLanguageCode);
  }
  return !isExtendedLanguageTabComplete(formValues, languageCode);
}

/** 切换默认语言：根级 ↔ extendedLang 字段对调 */
export function swapDefaultLanguage(form, currentDefault, nextDefault) {
  if (!form || !currentDefault || !nextDefault || currentDefault === nextDefault) {
    return;
  }

  const values = form.getFieldsValue(true);
  const extendedLang = { ...(values.extendedLang || {}) };

  extendedLang[currentDefault] = {
    soft_name: values.app_name || "",
    soft_baike_name: extendedLang[currentDefault]?.soft_baike_name || "",
    soft_brief: extendedLang[currentDefault]?.soft_brief || "",
    soft_logo: values.logo_pic || [],
    soft_snapshot: values.running_pic || [],
    video_url: extendedLang[currentDefault]?.video_url || "",
    soft_description: values.feature || "",
  };

  const nextValues = extendedLang[nextDefault] || {};

  delete extendedLang[nextDefault];

  form.setFieldsValue({
    app_name: nextValues.soft_name || "",
    logo_pic: nextValues.soft_logo || [],
    running_pic: nextValues.soft_snapshot || [],
    feature: nextValues.soft_description || "",
    extendedLang,
  });
}

export function validateInstallPackage(formValues = {}, uploadType) {
  if (uploadType === 0 || uploadType === "0") {
    if (!formValues.soft_download_url) {
      return "请输入安装包下载链接";
    }
    if (!/^(https):\/\/.*\.(exe|zip|msi)$/.test(formValues.soft_download_url)) {
      return "请输入 https 地址，文件名后缀 exe、msi 或 zip";
    }
    return null;
  }

  if (!Array.isArray(formValues.real_file_name) || !formValues.real_file_name.length) {
    return "请上传安装包文件";
  }
  return null;
}

/** Portal-04 save body */
export function buildIntlSavePayload({
  formValues = {},
  defaultLanguage,
  selectedLanguages = [],
  regionState = {},
  saveContext = {},
}) {
  const { detailMeta = {}, detailRaw = {}, oss = {} } = saveContext;

  const payload = {
    id: detailMeta.id,
    app_id: detailMeta.app_id,
    app_name: formValues.app_name,
    app_version: formValues.app_version,
    app_cate_id:
      resolveIntlAppCateId(formValues) ??
      resolveIntlAppCateId(detailRaw),
    language: INTL_LEGACY_LANGUAGE_MULTI_ID,
    upload_type: Number(formValues.upload_type ?? 0),
    certificate_pic: mapFileListToImageUrls(formValues.certificate_pic),
    logo_pic: mapFileListToImageUrls(formValues.logo_pic),
    running_pic: mapFileListToImageUrls(formValues.running_pic),
    feature: formValues.feature || "",
    new_feature: formValues.new_feature || "",
    install_params: formValues.install_params || "",
    os_osbit: (formValues.os_osbit || []).join(","),
    run_envs: (formValues.run_envs || []).join(","),
    fee: Number(formValues.fee),
    free_trial_days: Number(formValues.free_trial_days ?? 0),
  };

  if (payload.upload_type === 0) {
    payload.soft_download_url = formValues.soft_download_url;
  } else if (Array.isArray(formValues.real_file_name) && formValues.real_file_name.length) {
    payload.real_file_name = formValues.real_file_name[0].name;
    payload.app_pkg_key = oss.app_pkg_key || detailMeta.app_pkg_key || detailRaw.app_pkg_key || "";
  }

  const defaultLanguageItem = {
    language_code: defaultLanguage,
    soft_name: formValues.app_name,
    soft_baike_name: "",
    soft_brief: "",
    soft_logo: mapFileListToImageUrls(formValues.logo_pic),
    soft_snapshot: mapFileListToSoftSnapshot(formValues.running_pic),
    video_url: "",
    soft_description: formValues.feature || "",
  };

  const softMultiLanguage = [
    defaultLanguage,
    ...selectedLanguages.filter((code) => code !== defaultLanguage),
  ]
    .filter(Boolean)
    .map((code, index) => {
      if (index === 0) {
        return defaultLanguageItem;
      }

      const item = formValues.extendedLang?.[code] || {};
      return {
        language_code: code,
        soft_name: item.soft_name,
        soft_baike_name: item.soft_baike_name || "",
        soft_brief: item.soft_brief || "",
        soft_logo: mapFileListToImageUrls(item.soft_logo),
        soft_snapshot: mapFileListToSoftSnapshot(item.soft_snapshot),
        video_url: item.video_url || "",
        soft_description: item.soft_description || "",
      };
    });

  payload.soft_multi_language = softMultiLanguage;
  payload.white_list_region =
    regionState.mode === "global"
      ? [...(regionState.allCountryCodes || [])]
      : [...(regionState.selectedCountryCodes || [])];

  return payload;
}
